import { Navigation, Wind, Gauge, Target } from "lucide-react";

const CAT_COLOR = (kt) => {
  if (!kt) return "#38BDF8";
  if (kt >= 113) return "#FF0055";
  if (kt >= 83) return "#EF4444";
  if (kt >= 64) return "#F97316";
  if (kt >= 34) return "#FBBF24";
  return "#38BDF8";
};

export default function HurricaneTracker({ data }) {
  const storms = data?.storms || [];
  const threat = data?.barbados_threat;
  return (
    <div data-testid="hurricane-tracker-panel" className="glass rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-cyan" />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">Hurricane Tracker</h2>
        </div>
        <span className="font-mono-t text-[9px] uppercase tracking-widest text-slate-500">NOAA NHC</span>
      </div>

      <div
        data-testid="hurricane-distance-indicator"
        className="rounded-md px-3 py-2 border"
        style={threat
          ? { background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.5)" }
          : { background: "rgba(16,185,129,0.1)", borderColor: "rgba(16,185,129,0.4)" }}
      >
        {threat ? (
          <div>
            <div className="font-heading font-bold text-base text-red-400 uppercase flex items-center gap-1.5">
              <Target className="w-4 h-4" /> {threat.name} approaching
            </div>
            <div className="font-mono-t text-[10px] text-slate-300 mt-0.5">
              {threat.class_label} · {threat.distance_km} km from Bridgetown · bearing {threat.bearing_to_barbados}°
            </div>
            {threat.barbados_in_cone && (
              <div data-testid="barbados-in-cone" className="font-mono-t text-[10px] font-bold text-red-300 mt-1 uppercase tracking-wide">
                Barbados inside {threat.cone_source === "NHC_OFFICIAL" ? "official NHC" : "projected"} cone
              </div>
            )}
          </div>
        ) : (
          <div className="font-heading font-semibold text-sm text-emerald-400 uppercase">
            No active threat to Barbados
          </div>
        )}
      </div>

      <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
        {storms.length === 0 && (
          <div className="font-mono-t text-[11px] text-slate-500 text-center py-3">No active Atlantic systems</div>
        )}
        {storms.map((s) => (
          <div key={s.id} data-testid={`storm-${s.id}`} className="bg-slate-900/60 border border-border rounded-md px-2.5 py-1.5">
            <div className="flex items-center justify-between">
              <span className="font-heading font-semibold text-xs text-slate-100 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: CAT_COLOR(s.intensity_kt) }} />
                {s.name}
              </span>
              <span className="font-mono-t text-[10px] text-slate-400">{s.distance_km} km</span>
            </div>
            <div className="font-mono-t text-[9px] text-slate-500 uppercase tracking-wide mt-0.5 flex items-center gap-2">
              <span>{s.class_label}</span>
              {s.intensity_kt && <span className="flex items-center gap-0.5"><Wind className="w-2.5 h-2.5" />{s.intensity_kt}kt</span>}
              {s.pressure_mb && <span className="flex items-center gap-0.5"><Gauge className="w-2.5 h-2.5" />{s.pressure_mb}mb</span>}
            </div>
            <div data-testid={`storm-cone-source-${s.id}`} className="font-mono-t text-[9px] uppercase tracking-wide mt-0.5"
              style={{ color: s.cone_source === "NHC_OFFICIAL" ? "#e2e8f0" : "#64748b" }}>
              {s.cone_source === "NHC_OFFICIAL" ? `NHC cone · adv ${s.advisory || "—"}` : "Engine-projected cone"}
              {s.closest_approach_km != null && ` · CPA ${s.closest_approach_km} km`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
